import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type QueryType } from '~/constants/search';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PublicAssetModalNavigationOpenParams = {
  clipId: string;
  /**
   * Asset-modal deep-link params (amTab, timestamp, …). Implementations that navigate by URL write
   * them onto the route; implementations that navigate by state may ignore them — the modal still
   * opens, the deep-linked panel just isn't pre-selected.
   */
  query?: QueryType;
};

export type PublicAssetModalNavigationContextValue = {
  /** The clip whose public asset modal is open, or `null` when no modal is open. */
  activeClipId: string | null;
  /** Open the public asset modal for a clip. */
  onOpenAssetModal: (params: PublicAssetModalNavigationOpenParams) => void;
  /** Close the open public asset modal. */
  onCloseAssetModal: () => void;
};

const defaultContextValue: PublicAssetModalNavigationContextValue = {
  activeClipId: null,
  onOpenAssetModal: () => {},
  onCloseAssetModal: () => {},
};

const PublicAssetModalNavigationContext = createContext<PublicAssetModalNavigationContextValue>(defaultContextValue);

/*
 * The public viewer's asset-modal navigation — which asset's modal is open (`activeClipId`), open
 * one, close it. REQUIRED, supplied by whichever surface mounts the viewer tree.
 *
 * The tree itself is surface-blind: shared components and hooks read this contract without knowing
 * whether "open the asset modal" means pushing a `/c/{clipId}` route or flipping editor state. The
 * URL behavior is not a privileged default living inside shared code — it is one of two
 * implementations:
 *
 * - PublicUrlAssetModalNavigationProvider — the `/c/{clipId}` route segment plus query-param
 *   hygiene, mounted by every surface whose URLs work: both public board trees, the public clip
 *   page, the public collection route, and the editor's Preview iframe on its own preview routes.
 * - ShowcaseEditorAssetModalNavigationProvider — editor state, for the Edit tab's inline
 *   canvas, where pushing a route would eject the author.
 *
 * Why this contract stays viewer-wide when board drills, page navigation, and the rest moved to
 * per-tree contracts (PublicBoardProvider, PublicShowcaseProvider): opening an asset modal
 * is the one navigation concern every public surface — board, clip, and collection links alike —
 * really has, and the members die with the feature. The sole open-side reader
 * (useGoToPublicAssetPage) renders in every tree, including inside AssetModal itself for
 * next/previous-asset moves; delete the public asset modal and every member here is orphaned
 * nowhere smaller. Still on the optional PublicViewerHostProvider: only the data layer's
 * board-scope selector (useCurrentBoardIdSelector), which cannot read a public-only contract
 * because its consumers (useBoardSearchParams and the view-item hooks) also serve the private app.
 */
export const usePublicAssetModalNavigation = () => {
  const ctx = useContext(PublicAssetModalNavigationContext);

  if (ctx === defaultContextValue) {
    const error = new Error('usePublicAssetModalNavigation must be used within PublicAssetModalNavigationProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Public asset modal navigation context not found',
    });
  }

  return ctx;
};

export type PublicAssetModalNavigationProviderProps = PublicAssetModalNavigationContextValue & {
  children: ReactNode;
};

/**
 * Carries a surface's asset-modal navigation bindings into the public viewer tree beneath it. The
 * bindings are passed in as props (rather than built here) so each surface owns its own behavior —
 * this file never imports any surface's code.
 */
export const PublicAssetModalNavigationProvider = memo(
  ({ activeClipId, children, onCloseAssetModal, onOpenAssetModal }: PublicAssetModalNavigationProviderProps) => {
    const contextValue = useMemo<PublicAssetModalNavigationContextValue>(
      () => ({
        activeClipId,
        onCloseAssetModal,
        onOpenAssetModal,
      }),
      [activeClipId, onCloseAssetModal, onOpenAssetModal],
    );

    return (
      <PublicAssetModalNavigationContext.Provider value={contextValue}>
        {children}
      </PublicAssetModalNavigationContext.Provider>
    );
  },
);

PublicAssetModalNavigationProvider.displayName = 'PublicAssetModalNavigationProvider';
